/**
 * Mode-aware adapters for the keyword benchmarking endpoints.
 *
 * Each statistics endpoint (institution / researcher / unit / venue) returns
 * the same shape — `[{ keyword: [{ <id_field>, name, photo_url, ...metrics }] }]` —
 * but with a different id field. To keep the existing chart components
 * (StackedBarChart, PieChart, BenchmarkHeatmap, BreakdownTable, …) reusable
 * without per-mode forks, we alias the entity id field to `institution_id`
 * before handing the payload off.
 *
 * Modes:
 *   - institution → institution_id (passthrough)
 *   - researcher  → researcher_id  → institution_id
 *   - unit        → unit_id        → institution_id
 *   - venue       → venue_id       → institution_id
 */

export const BENCHMARK_MODES = {
  RESEARCH_TOPICS_OVERVIEW: "researchTopicsOverview",
  INSTITUTION: "institution",
  RESEARCHER: "researcher",
  UNIT: "unit",
  VENUE: "venue",
};

export const ENTITY_ID_KEY_BY_MODE = {
  [BENCHMARK_MODES.INSTITUTION]: "institution_id",
  [BENCHMARK_MODES.RESEARCHER]: "researcher_id",
  [BENCHMARK_MODES.UNIT]: "unit_id",
  [BENCHMARK_MODES.VENUE]: "venue_id",
};

export const ENTITY_LABEL_BY_MODE = {
  [BENCHMARK_MODES.INSTITUTION]: { singular: "institution", plural: "institutions" },
  [BENCHMARK_MODES.RESEARCHER]: { singular: "researcher", plural: "researchers" },
  [BENCHMARK_MODES.UNIT]: { singular: "unit", plural: "units" },
  [BENCHMARK_MODES.VENUE]: { singular: "venue", plural: "venues" },
};

export const objectTypeFromBenchmarkMode = (mode) => {
  if (mode === BENCHMARK_MODES.INSTITUTION) return "institution";
  if (mode === BENCHMARK_MODES.RESEARCHER) return "researcher";
  if (mode === BENCHMARK_MODES.UNIT) return "unit";
  if (mode === BENCHMARK_MODES.VENUE) return "venue";
  return null;
};

/**
 * Aliases the entity id field to `institution_id` so the existing
 * institution-centric chart components can render any benchmark mode.
 * Returns the original array untouched when already in institution shape.
 */
export const normalizeRawDataForCharts = (rawData, entityIdKey) => {
  if (!Array.isArray(rawData) || !rawData.length) return [];
  if (!entityIdKey || entityIdKey === "institution_id") return rawData;

  return rawData.map((entry) => {
    const keyword = Object.keys(entry || {})[0];
    if (!keyword) return entry;
    const list = entry[keyword] || [];
    return {
      [keyword]: list.map((item) => ({
        ...item,
        institution_id:
          item.institution_id != null ? item.institution_id : item?.[entityIdKey],
      })),
    };
  });
};

/**
 * Pulls a year-keyed object field (e.g. publication_count_by_year,
 * grants_count_by_year) off each entity and returns one series per entity.
 * Empty arrays or missing fields produce no series entry (the chart hides).
 * When data spans more than `maxYears` years, only the most recent ones are kept.
 */
export const collectMetricByYearSeries = (
  rawData,
  entityIds,
  entityIdKey,
  fieldName,
  maxYears = 20
) => {
  if (!Array.isArray(rawData) || !rawData.length || !entityIdKey || !fieldName)
    return [];

  const byEntity = new Map();

  rawData.forEach((entry) => {
    const keyword = Object.keys(entry || {})[0];
    if (!keyword) return;
    (entry[keyword] || []).forEach((item) => {
      const id = item?.[entityIdKey];
      if (id == null) return;
      const raw = item[fieldName];
      // Treat missing, empty array, or non-object as "no data".
      if (!raw || Array.isArray(raw) || typeof raw !== "object") return;
      const points = Object.entries(raw)
        .map(([year, value]) => {
          const y = Number(year);
          const v = Number(value);
          return Number.isFinite(y) && Number.isFinite(v) ? { year: y, value: v } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.year - b.year);
      if (!points.length) return;

      const prev = byEntity.get(id);
      if (!prev) {
        byEntity.set(id, {
          id,
          name: item.name || String(id),
          photo_url: item.photo_url || null,
          points,
        });
      } else {
        const merged = new Map(prev.points.map((p) => [p.year, p.value]));
        points.forEach(({ year, value }) => {
          merged.set(year, (merged.get(year) || 0) + value);
        });
        prev.points = Array.from(merged.entries())
          .map(([year, value]) => ({ year, value }))
          .sort((a, b) => a.year - b.year);
      }
    });
  });

  const wantedIds = (entityIds || []).filter((id) => byEntity.has(id));
  const result = (wantedIds.length ? wantedIds : Array.from(byEntity.keys())).map(
    (id) => byEntity.get(id)
  );

  // Trim to the most recent maxYears across all series.
  if (maxYears > 0 && result.length) {
    const allYears = new Set();
    result.forEach(({ points }) => points.forEach((p) => allYears.add(p.year)));
    const sorted = Array.from(allYears).sort((a, b) => a - b);
    if (sorted.length > maxYears) {
      const cutoff = sorted[sorted.length - maxYears];
      result.forEach((s) => {
        s.points = s.points.filter((p) => p.year >= cutoff);
      });
    }
  }

  return result;
};
